import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

function normalizeLabel(name) {
  const raw = String(name || "").trim().toLowerCase();
  if (raw === "sim") return "Sim";
  if (raw === "não" || raw === "nao") return "Não";
  return String(name || "");
}

export default function ReleaseOriginDonutChart({
  data = [],
  loading = false,
  title = "Releases",
  filename = "releases",
  exportMode = false,
}) {
  const chartRef = useRef(null);
  const CARD_MIN_H = 360;
  const CHART_H = 360;

  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const normalizedData = useMemo(() => {
    return (data || []).map((item) => ({
      name: normalizeLabel(item?.name ?? item?.value),
      y: Number(item?.y ?? item?.total ?? 0),
    }));
  }, [data]);

  const total = useMemo(
    () => normalizedData.reduce((acc, item) => acc + (Number(item?.y) || 0), 0),
    [normalizedData]
  );

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "visible",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "pie",
        height: exportMode ? 560 : CHART_H,
        spacing: exportMode ? [0, 36, 0, 36] : [24, 16, 16, 24],
        backgroundColor: "transparent",
        animation: !exportMode,
        marginTop: exportMode ? 0 : 64,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: {
          fontSize: "14px",
          fontWeight: "800",
          color: "#614a9e",
        },
      },

      exporting: { enabled: false },

      tooltip: {
        useHTML: true,
        pointFormatter: function () {
          const pct = total > 0 ? Math.round((this.y / total) * 100) : 0;
          return `<b>${this.name}</b><br/><b>${this.y}</b> (${pct}%)`;
        },
      },

      plotOptions: {
        pie: {
          innerSize: exportMode ? "72%" : "65%",
          size: exportMode ? "86%" : "75%",
          center: exportMode ? ["50%", "50%"] : ["49%", "50%"],
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            distance: exportMode ? 22 : 14,
            crop: false,
            overflow: "allow",
            allowOverlap: true,
            formatter: function () {
              return `${this.point.name}: <b>${this.y}</b>`;
            },
            style: {
              fontSize: exportMode ? "15px" : "12px",
              fontWeight: "700",
              color: "var(--text-color)",
              textOutline: "none",
            },
          },
          showInLegend: false,
        },
      },

      legend: exportMode
        ? { enabled: false }
        : {
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            symbolRadius: 99,
            itemStyle: {
              fontWeight: "700",
              fontSize: "12px",
              color: "var(--text-color)",
            },
            labelFormatter: function () {
              return `${this.name} - ${this.y}`;
            },
          },

      series: [
        {
          name: "Origem",
          data: normalizedData,
          colors: ["#614a9e", "#C4B5FD", "#E9D5FF", "#A78BFA"],
        },
      ],

      credits: { enabled: false },
    };
  }, [normalizedData, title, exportMode, total]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();

          if (chart.renderTo) {
            chart.renderTo.style.overflow = "visible";
          }

          if (chart.container) {
            chart.container.style.overflow = "visible";
            const svg = chart.container.querySelector("svg");
            if (svg) {
              svg.style.overflow = "visible";
              svg.setAttribute("overflow", "visible");
            }
          }
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, normalizedData]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,
    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando origem dos releases.">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{
            style: {
              height: exportMode ? 560 : CHART_H,
              overflow: exportMode ? "visible" : "hidden",
            },
          }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
